import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";

import { SERVICE_TYPES } from "./constants";
import { useServices, useBranches } from "./hooks/useServices";
import { useServiceFilters } from "./hooks/useServiceFilters";
import { usePagination } from "./hooks/usePagination";
import { useSnackbar } from "./hooks/useSnackbar";
import { C, SCROLL } from "./theme";

import ServiceKPIs from "./components/ServiceKPIs";
import ServiceFilters from "./components/ServiceFilters";
import ServiceList from "./components/ServiceList";
import ServicePagination from "./components/ServicePagination";
import ServiceDetailPanel from "./components/ServiceDetailPanel";
import CreateServiceModal from "./components/modals/CreateServiceModal";
import EditServiceModal from "./components/modals/EditServiceModal";
import ModalConfirmUniversal from "../../components/ModalConfirmUniversal";
import { getBusinessDateParts } from "../../utils/dateUtils";

export default function Servicios() {
  const getCurrentMonth = () => {
    const { year, month } = getBusinessDateParts();
    return `${year}-${String(month + 1).padStart(2, "0")}`;
  };

  const [monthFilter, setMonthFilter] = useState(() => getCurrentMonth());
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const {
    servicios,
    clientes,
    tecnicos,
    createService,
    updateService,
    deleteService,
    loadingSelects,
    setLoadingSelects,
    loadClientes,
    loading,
  } = useServices(monthFilter);

  const { sucursales, loadBranchesByClient, setSucursales } = useBranches();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const filters = useServiceFilters(servicios, monthFilter, setMonthFilter);
  const pagination = usePagination(filters.filtered, 50);

  useEffect(() => {
    if (!loading && initialLoad) setInitialLoad(false);
  }, [loading, initialLoad]);

  useEffect(() => {
    pagination.resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.search,
    filters.tecnicoFilter,
    filters.zonaFilter,
    monthFilter,
    filters.orderBy,
    filters.orderDirection,
  ]);

  useEffect(() => {
    setSelected(null);
    setDetailOpen(false);
  }, [monthFilter]);

  const handleCreate = async (data) => {
    if (!data.type || !data.client || !data.branch || !data.date) {
      showSnackbar("Completa los campos obligatorios.", "warning");
      return;
    }
    try {
      await createService(data);
      setCreateOpen(false);
      setSucursales([]);
      showSnackbar("Servicio creado correctamente.", "success");
    } catch (e) {
      showSnackbar(e.message || "Error creando servicio", "error");
    }
  };

  const handleEdit = async (payload) => {
    const id = selected?._id || selected?.id;
    if (!id) return;
    try {
      await updateService(id, payload);
      setEditOpen(false);
      setSelected(null);
      setDetailOpen(false);
      showSnackbar("Servicio actualizado.", "success");
    } catch (e) {
      showSnackbar(e.message || "Error actualizando servicio", "error");
    }
  };

  const handleDelete = async () => {
    const id = selected?._id || selected?.id;
    if (!id) return;
    try {
      await deleteService(id);
      setDeleteOpen(false);
      setSelected(null);
      showSnackbar("Servicio eliminado.", "success");
    } catch (e) {
      showSnackbar(e.message || "Error eliminando servicio", "error");
    }
  };

  const handleClientChange = async (clientId) => {
    try {
      setLoadingSelects(true);
      await loadBranchesByClient(clientId);
    } catch {
      showSnackbar("Error cargando sucursales", "error");
    } finally {
      setLoadingSelects(false);
    }
  };

  const deleteMessage = selected
    ? `¿Eliminar el servicio "${
        SERVICE_TYPES.find((t) => t.value === selected.type)?.label ?? selected.type
      }"?`
    : "";

  if (initialLoad && loading) {
    return (
      <Box
        sx={{
          width: "calc(100% + (var(--app-page-padding, 24px) * 2))",
          height: "calc(100vh - var(--app-nav-height, 56px))",
          m: "calc(var(--app-page-padding, 24px) * -1)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: C.primary }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "calc(100% + (var(--app-page-padding, 24px) * 2))",
          height: "calc(100vh - var(--app-nav-height, 56px))",
          minHeight: 0,
          m: "calc(var(--app-page-padding, 24px) * -1)",
          p: 2,
          bgcolor: "#fff",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AssignmentIcon sx={{ color: C.primary, fontSize: 28 }} />
              <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
                  Servicios
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.textSoft, mt: 0.3 }}>
                  Gestión mensual de servicios programados
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={async () => {
                setSucursales([]);
                try {
                  await loadClientes();
                  setCreateOpen(true);
                } catch {
                  showSnackbar("Error cargando clientes", "error");
                }
              }}
              sx={{
                textTransform: "none",
                bgcolor: C.primary,
                boxShadow: "none",
                "&:hover": { bgcolor: C.primary, boxShadow: "none" },
              }}
            >
              Nuevo servicio
            </Button>
          </Box>

          {/* KPIs + Filtros */}
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            <ServiceKPIs servicios={loading ? [] : servicios} />
            <Box sx={{ mt: 2 }}>
              <ServiceFilters
                search={filters.search}
                setSearch={filters.setSearch}
                tecnicoFilter={filters.tecnicoFilter}
                setTecnicoFilter={filters.setTecnicoFilter}
                zonaFilter={filters.zonaFilter}
                setZonaFilter={filters.setZonaFilter}
                monthFilter={filters.monthFilter}
                goToPreviousMonth={filters.goToPreviousMonth}
                goToNextMonth={filters.goToNextMonth}
                resetFilters={filters.resetFilters}
                hasActiveFilters={filters.hasActiveFilters}
                tecnicos={tecnicos}
                servicios={servicios}
                loadingMonth={loading}
                filteredCount={filters.filtered.length}
              />
            </Box>
          </Box>

          {/* Lista */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                }}
              >
                <CircularProgress size={36} sx={{ color: C.primary }} />
                <Typography sx={{ fontSize: 14, color: C.textSoft }}>
                  Cargando servicios...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ ...SCROLL, flex: 1, minHeight: 0, overflowY: "auto" }}>
                <ServiceList
                  services={pagination.paginatedItems}
                  technicians={tecnicos}
                  selected={selected}
                  orderBy={filters.orderBy}
                  orderDirection={filters.orderDirection}
                  onSort={filters.handleSort}
                  onSelect={(s) => {
                    setSelected(s);
                    setDetailOpen(true);
                  }}
                  onEdit={(s) => {
                    setSelected(s);
                    setDetailOpen(false);
                    setEditOpen(true);
                  }}
                  onDelete={(s) => {
                    setSelected(s);
                    setDeleteOpen(true);
                  }}
                />
              </Box>
            )}

            {!loading && (
              <ServicePagination {...pagination} />
            )}
          </Box>
        </Paper>
      </Box>

      <ServiceDetailPanel
        open={detailOpen}
        selected={selected}
        technicians={tecnicos}
        onClose={() => {
          setDetailOpen(false);
          setSelected(null);
        }}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDeleteOpen(true)}
      />

      <CreateServiceModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setSucursales([]);
        }}
        onSubmit={handleCreate}
        clientes={clientes}
        sucursales={sucursales}
        tecnicos={tecnicos}
        loadingSelects={loadingSelects}
        onClientChange={handleClientChange}
      />

      <EditServiceModal
        open={editOpen}
        selected={selected}
        tecnicos={tecnicos}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEdit}
      />

      <ModalConfirmUniversal
        open={deleteOpen}
        title="Eliminar servicio"
        message={deleteMessage}
        confirmText="Eliminar"
        color="error"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
